import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export const MAP_STYLE_OPTIONS = [
  { key: "street", label: "Plan" },
  { key: "hybrid", label: "Satellite" },
  { key: "dark",   label: "Sombre" },
];

/**
 * Shared Plan / Sombre / Satellite dropdown — used by both the Carte
 * (HeatmapCommandCenter) and Planification command bars.
 *
 * Root-cause fix (Iter94): both bars used to render this dropdown
 * inline with `position: absolute` INSIDE the horizontally-scrollable
 * toolbar row (`overflow-x-auto`, needed so the row's OTHER pills can
 * scroll on narrow screens). Per the CSS spec, setting `overflow-x` to
 * anything but `visible` forces `overflow-y` to compute as `auto` too
 * if it was `visible` — so that same row silently became a VERTICAL
 * clipping container as well, cutting off the dropdown the instant it
 * tried to extend below the row. It wasn't a z-index problem (the
 * dropdown's z-index was fine) and it wasn't rendering "behind" the
 * map — it was being clipped by its own scrollable parent before it
 * ever reached the map's stacking context.
 *
 * Fix: portal the open menu to `document.body` and position it with
 * `position: fixed` + coordinates read from the trigger button's own
 * `getBoundingClientRect()`. This sidesteps every ancestor's overflow/
 * transform/stacking-context quirks instead of fighting them one by
 * one — closes on scroll/resize/outside-click rather than re-tracking
 * the anchor, which keeps it correct without a resize-observer.
 */
export const MapStyleSwitcher = ({
  styleKind,
  onChange,
  align = "left",
  icon: Icon,
  toggleTestId,
  backdropTestId,
  menuTestId,
  optionTestId,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const MENU_W = 130;
    let left = align === "right" ? r.right - MENU_W : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_W - 8));
    setPos({ top: r.bottom + 4, left });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const current = MAP_STYLE_OPTIONS.find((s) => s.key === styleKind)?.label || "Satellite";

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        data-testid={toggleTestId}
        onClick={() => (open ? setOpen(false) : openMenu())}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border border-zinc-700 text-zinc-400 hover:text-zinc-200"
      >
        {Icon && <Icon className="w-3 h-3" />} {current}
      </button>
      {open && pos && createPortal(
        <>
          <button
            type="button"
            aria-label="Fermer le sélecteur de style"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[9998] cursor-default bg-transparent"
            data-testid={backdropTestId}
          />
          <div
            role="listbox"
            aria-label="Style de carte"
            data-testid={menuTestId}
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
            className="min-w-[130px] bg-[#111418] border border-zinc-700 shadow-xl overflow-hidden"
          >
            {MAP_STYLE_OPTIONS.map((opt) => {
              const active = styleKind === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="option"
                  aria-selected={active}
                  data-testid={optionTestId ? optionTestId(opt.key) : undefined}
                  onClick={() => { onChange(opt.key); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors ${
                    active ? "bg-emerald-500 text-black" : "text-zinc-300 hover:bg-zinc-800"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
